import styled, {css} from 'styled-components';

import type {ButtonsContainerStyledProps} from './types';

import CloseSvg from '../../../../assets/icons/close.svg';
import {ms, msi} from '../../../../styles/normalizeSize';
import {getColor, getFontWeight} from '../../../../styles/utils';
import {Button} from '../../../../uikit/Button';
import {LottieView} from '../../../../uikit/LottieView';
import {Text} from '../../../../uikit/Text';
import {View} from '../../../../uikit/View';
import {platform} from '../../../../utils/platform';
import {container} from './styles.container';

const containerBase = css`
  border-radius: ${ms(15)};
  padding-top: ${ms(17)};
  background-color: ${getColor('background1')};
  z-index: 7;
  overflow: hidden;
  margin: 0 ${ms(20)};
  ${container};
`;

export const containerWeb = css<{$animationDuration: number}>`
  ${containerBase};

  opacity: 0;
  transform: scale(1);
  transition:
    opacity ${p => p.$animationDuration}ms,
    transform ${p => p.$animationDuration}ms;
  display: flex;
  flex-direction: column;

  &.alert-appear {
    opacity: 0;
    transform: scale(0.8);
  }

  &.alert-appear-active {
    opacity: 1;
    transform: scale(1);
  }

  &.alert-appear-done {
    opacity: 1;
    transform: scale(1);
  }

  &.alert-exit {
    opacity: 1;
    transform: scale(1);
  }

  &.alert-exit-active {
    opacity: 0;
    transform: scale(0.8);
  }

  &.alert-exit-done {
    opacity: 0;
    transform: scale(0.8);
  }
`;

export const containerNative = css`
  ${containerBase};
`;

export const TitleContainer = styled(View)<{
  withPaddingForCloseButton?: boolean;
}>`
  padding: 0 ${ms(20)} ${ms(15)};
  align-items: center;
  justify-content: center;

  ${p =>
    p.withPaddingForCloseButton &&
    css`
      padding-inline-end: ${ms(20 + 28)};
    `}
`;

export const Title = styled(Text).attrs({
  as: platform.isNative ? undefined : 'h6',
})<{align?: 'center' | 'left'}>`
  font-size: ${ms(20)};
  line-height: ${ms(28)};
  text-align: ${p => p.align || 'center'};
  font-weight: ${getFontWeight('bold')};
  color: ${getColor('text1')};
  flex: 1;
`;

export const CloseButton = styled(Button).attrs({
  scheme: 'gray',
})`
  width: ${ms(28)};
  height: ${ms(28)};
  border-radius: ${ms(14)};
  position: absolute;
  right: ${ms(20)};
  top: ${ms(20)};
  align-items: center;
  justify-content: center;
  padding: 0;
`;

export const CloseIcon = styled(CloseSvg).attrs(p => ({
  fill: getColor('icon3')(p),
}))`
  width: ${ms(20)};
  height: ${ms(20)};
`;

const messageContainerBase = css`
  padding: 0 ${ms(20)};
  margin-bottom: ${ms(20)};
  display: flex;
  flex-direction: column;
`;

export const messageContainerWeb = css<{$variant?: 'notification' | 'promo'}>`
  ${messageContainerBase};
  overflow-y: auto;

  ${p => {
    if (p.$variant === 'notification')
      return css`
        margin-bottom: 0;
      `;
  }}
`;

export const messageContainerNative = css`
  ${messageContainerBase};
  flex-grow: 0;
`;

export const Message = styled(Text).attrs({
  as: platform.isNative ? undefined : 'p',
})`
  font-size: ${ms(15)};
  line-height: ${ms(20)};
  letter-spacing: ${ms(-0.2)};
  text-align: center;
  color: ${getColor('text1')};
`;

type SButtonProps = ButtonsContainerStyledProps & {
  $isFirst?: boolean;
  $isLast?: boolean;
};

const sButtonStyles = css<SButtonProps>`
  margin-left: ${p => ms(p.$isFirst ? 0 : 10)};

  ${p =>
    p.$variant !== 'promo' &&
    ((platform.isNative && p.$buttonsCount <= 2) || platform.isWeb) &&
    css`
      flex: 1;
    `}

  ${p => {
    if (platform.isNative && (p.$variant === 'promo' || p.$buttonsCount > 2)) {
      return `
        margin-left: 0;
        margin-bottom: ${p.$isLast ? 0 : msi(10)}px;
      `;
    }
  }}
`;

const SButton = styled(Button)<SButtonProps>`
  ${sButtonStyles};
`;

export {SButton as Button};

const SButtonV2 = styled(Button.V2)<SButtonProps>`
  ${sButtonStyles};
`;

export {SButtonV2 as ButtonV2};

const buttonsContainerBase = css`
  flex-direction: row;
  justify-content: flex-end;
  margin: 0 ${ms(20)} ${ms(20)};
`;

const fullWidthButtonsContainerWeb = css`
  flex-direction: column;
`;

export const buttonsContainerWeb = css<ButtonsContainerStyledProps>`
  ${buttonsContainerBase};

  ${p => {
    if (p.$variant === 'promo') {
      // reusing ${SButton} styles doesn't work!
      return `
      ${fullWidthButtonsContainerWeb}
      ${SButton} {
        margin-left: 0;
        margin-bottom: ${ms(10)};
      }
      ${SButton}:last-child {
        margin-bottom: 0;
      }
      `;
    } else if (p.$buttonsCount > 3) {
      return `
      @media (max-width: 600px) {
        ${fullWidthButtonsContainerWeb}
        ${SButton} {
          margin-left: 0;
          margin-bottom: ${ms(10)};
        }
        ${SButton}:last-child {
          margin-bottom: 0;
        }
      }
      `;
    } else if (p.$buttonsCount > 2) {
      return `
      @media (max-width: 430px) {
        ${fullWidthButtonsContainerWeb}
        ${SButton} {
          margin-left: 0;
          margin-bottom: ${ms(10)};
        }
        ${SButton}:last-child {
          margin-bottom: 0;
        }
      }
      `;
    }
  }}
`;

export const buttonsContainerNative = css<ButtonsContainerStyledProps>`
  ${buttonsContainerBase};

  ${p => {
    if (p.$variant === 'promo' || p.$buttonsCount > 2) {
      return `
      ${fullWidthButtonsContainerWeb}
      `;
    }
  }}
`;

export const AnimationContainer = styled(View)`
  justify-content: center;
  margin-block-end: ${ms(25)};
`;

export const Animation = styled(LottieView)<{size: number}>`
  height: ${p => ms(p.size)};
  width: 100%;
  align-self: center;
`;
